import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import ClientDiscountService from '../../services/ClientDiscountService';
import DateField from '../common/DateField';
import { isValidDateValue } from '../common/PolishDateInput';
import { displayDate, displayDateTime } from '../common/datePresentation';
import { buildSchoolYearRange, getCurrentSchoolYearStartYear } from '../common/schoolYear';
import { canReadPayments } from './rbac';
import { discountToday, latestDiscountVersions, useDiscountPolicy } from './discountManagement';
import './clientDiscounts.scss';

const emptyGrant = () => {
  const { fromDate, tillDate } = buildSchoolYearRange(getCurrentSchoolYearStartYear());
  return { typeId: '', percent: '', validFrom: fromDate, validTo: tillDate, studentScope: 'all', studentIds: [], reason: '', prepaymentConfirmed: false };
};

export default function ClientDiscounts({ clientId, students, user, service }) {
  const { t: tr } = useTranslation();
  const t = (key, params) => tr(`billing:discounts.${key}`, params);
  const allowed = canReadPayments(user);
  const policy = useDiscountPolicy(async () => {
    const [types, grants] = await Promise.all([service.getTypes(), service.getGrants(clientId)]);
    return { types, grants };
  }, allowed);
  const [draft, setDraft] = useState(null);
  const types = policy.data ? latestDiscountVersions(policy.data.types.versions, 'typeId') : [];
  const grants = policy.data ? latestDiscountVersions(policy.data.grants.versions, 'grantId').filter(grant => !grant.deletedAt) : [];
  const usedGrantIds = policy.data ? policy.data.grants.usedGrantIds || [] : [];
  const selectedType = draft && policy.data && policy.data.types.versions
    .filter(type => type.typeId === draft.typeId && (!type.effectiveFrom || type.effectiveFrom <= draft.validFrom))
    .sort((a, b) => b.version - a.version)[0];
  const change = (key, value) => setDraft(current => ({ ...current, [key]: value }));
  const errors = [];
  const addError = (field, key, params) => errors.push({ field, message: t(`validation.${key}`, params) });
  if (draft) {
    if (!draft.reason.trim()) addError(9, 'reason');
    if (draft.intent === 'end') {
      if (!isValidDateValue(draft.endedFrom)) addError(1, 'endDate');
      else if (draft.endedFrom <= draft.validFrom) addError(1, 'endAfterStart', { date: displayDate(draft.validFrom) });
    } else if (draft.intent !== 'delete') {
      if (!draft.typeId) addError(2, 'type');
      if (draft.percent === '' || !Number.isFinite(Number(draft.percent)) || Number(draft.percent) < 0 || Number(draft.percent) > 100) addError(3, 'percent');
      if (!isValidDateValue(draft.validFrom)) addError(4, 'startDate');
      else {
        if (draft.originalFrom && draft.validFrom <= draft.originalFrom) addError(4, 'changeAfterStart', { date: displayDate(draft.originalFrom) });
        if (draft.typeId && !selectedType) {
          const firstType = (policy.data ? policy.data.types.versions : []).filter(type => type.typeId === draft.typeId)
            .sort((a, b) => String(a.effectiveFrom || '').localeCompare(b.effectiveFrom || ''))[0];
          if (firstType) addError(4, 'typeStartsLater', { date: displayDate(firstType.effectiveFrom) });
          else addError(2, 'type');
        } else if (selectedType && selectedType.endedFrom && draft.validFrom >= selectedType.endedFrom) {
          addError(4, 'typeEnded', { date: displayDate(selectedType.endedFrom) });
        }
      }
      if (draft.validTo && !isValidDateValue(draft.validTo)) addError(5, 'endDate');
      else if (draft.validTo && isValidDateValue(draft.validFrom) && draft.validTo < draft.validFrom) addError(5, 'dateOrder');
      if (selectedType && selectedType.requiresValidTo && !draft.validTo) addError(5, 'endRequired');
      if (selectedType && selectedType.requiresPrepayment && !draft.prepaymentConfirmed) addError(8, 'prepayment');
      if (draft.studentScope !== 'all' && !draft.studentIds.length) addError(6, 'students');
    }
  }
  const deletionBlocked = draft && draft.intent === 'delete' && usedGrantIds.includes(draft.grantId);
  const valid = draft && !errors.length && !deletionBlocked;
  const submit = async (event) => {
    event.preventDefault();
    if (!valid) return;
    const payload = {
      clientId,
      expectedRevision: policy.data.grants.revision,
      intent: draft.intent || 'create',
      ...(draft.grantId ? { grantId: draft.grantId } : {}),
      reason: draft.reason.trim(),
      ...(draft.intent !== 'delete' && (draft.intent === 'end' ? { endedFrom: draft.endedFrom } : {
        typeId: draft.typeId,
        percentBasisPoints: Math.round(Number(draft.percent) * 100),
        validFrom: draft.validFrom,
        validTo: draft.validTo || undefined,
        studentScope: draft.studentScope,
        studentIds: draft.studentScope === 'all' ? [] : draft.studentIds,
        prepaymentConfirmed: draft.prepaymentConfirmed,
      })),
    };
    if (await policy.save(service.saveGrant, payload)) setDraft(null);
  };
  const status = (grant) => {
    const type = types.find(item => item.typeId === grant.typeId);
    const today = discountToday();
    if ((grant.endedFrom && grant.endedFrom <= today) || (type && type.endedFrom && type.endedFrom <= today) || (grant.validTo && grant.validTo < today)) return 'ended';
    const started = policy.data.grants.versions.some(version => version.grantId === grant.grantId && version.validFrom <= today && !version.endedFrom);
    return started ? 'active' : 'future';
  };
  if (!allowed) return null;
  return (
    <section
      className="card mb-3 client-discounts"
      id="discounts"
      aria-label={t('title')}
    >
      <div
        className="card-body"
      >
        <div className="client-discounts__heading">
          <h5 className="mb-0">{t('title')}</h5>
          {policy.data && !draft && (
          <button
            type="button"
            className="btn manage-button manage-red-button"
            disabled={policy.busy}
            onClick={() => setDraft(emptyGrant())}
          >{t('add')}
          </button>
          )}

        </div>
        <details className="client-discounts__rules"><summary>{t('rulesTitle')}</summary><p className="mb-2">{t('rule')}</p></details>
        {policy.error && (
        <div
          role="alert"
          className="alert alert-danger"
        >{t(policy.error)}
        </div>
        )}
        {(policy.error || !policy.data) && (
        <button
          type="button"
          className="btn manage-button manage-teal-button"
          disabled={policy.busy}
          onClick={policy.reload}
        >{t('reload')}
        </button>
        )}
        {policy.data && !grants.length && !draft && <p className="mb-0">{t('empty')}</p>}
        {grants.map(grant => (
          <div
            key={grant.grantId}
            className="client-discounts__grant"
          >
            <div className="client-discounts__summary">
              <div className="client-discounts__summary-line">
                <strong>{(types.find(type => type.typeId === grant.typeId) || {}).name || grant.typeId}: {grant.percentBasisPoints / 100}%</strong>
                <span className="badge badge-light">{t(status(grant))}</span>
              </div>
              <p className="mb-1">{grant.endedFrom ? `${t('endedFrom')}: ${displayDate(grant.endedFrom)}` : `${displayDate(grant.validFrom)} — ${displayDate(grant.validTo) || t('openEnd')}`}</p>
              <p className="small mb-1">{grant.studentScope === 'all' ? t('all') : (grant.studentIds || []).map(id => {
                const student = students.find(item => item.id === id);
                return student ? `${student.firstName} ${student.lastName}` : id;
              }).join(', ')}
              </p>
              <details className="small"><summary>{t('detailsTitle')}</summary>
                <p className="mb-1">{t('reason')}: {grant.reason}</p>
                {grant.prepaymentConfirmedAt && <p className="mb-1">{t('confirmed')}: {displayDateTime(grant.prepaymentConfirmedAt)}</p>}
              </details>
            </div>
            <div className="client-discounts__actions">
              <button
                type="button"
                className="btn manage-button manage-teal-button"
                disabled={policy.busy || !!draft || !!grant.endedFrom}
                onClick={() => setDraft({
                  ...grant,
                  intent: 'change',
                  originalFrom: grant.validFrom,
                  validFrom: '',
                  percent: String(grant.percentBasisPoints / 100),
                  validTo: grant.validTo || '',
                  studentIds: grant.studentIds || [],
                  prepaymentConfirmed: false,
                })}
              >{t('edit')}
              </button>
              <button
                type="button"
                className="btn manage-button manage-teal-button"
                disabled={policy.busy || !!draft || !!grant.endedFrom}
                onClick={() => setDraft({ ...grant, intent: 'end', endedFrom: '', reason: '' })}
              >{t('end')}
              </button>
              <div>
                <button
                  type="button"
                  className="btn manage-button manage-teal-button"
                  disabled={policy.busy || !!draft || usedGrantIds.includes(grant.grantId)}
                  aria-describedby={usedGrantIds.includes(grant.grantId) ? `discount-used-${grant.grantId}` : undefined}
                  onClick={() => setDraft({ ...grant, intent: 'delete', reason: '' })}
                >{t('delete')}
                </button>
                {usedGrantIds.includes(grant.grantId) && <small className="d-block" id={`discount-used-${grant.grantId}`}>{t('usedHint')}</small>}
              </div>
            </div>
          </div>
        ))}
        {draft && (
        <form
          onSubmit={submit}
          className="client-discounts__form"
        >
          <fieldset
            disabled={policy.busy}
          >
            {draft.intent === 'delete' && (
            <p className="alert alert-warning">{deletionBlocked ? t('alreadyUsed') : t('deleteHint', {
              name: (types.find(type => type.typeId === draft.typeId) || {}).name || draft.typeId,
              percent: draft.percentBasisPoints / 100,
            })}
            </p>
            )}
            <div className="client-discounts__grid">
              {draft.intent !== 'delete' && (draft.intent === 'end' ? (
                <label
                  className="client-discounts__field client-discounts__field--4"
                  htmlFor="ClientDiscounts-field-1"
                >
                  {t('endedFrom')}
                  <DateField
                    id="ClientDiscounts-field-1"
                    aria-label={t('endedFrom')}
                    className="form-control"
                    type="date"
                    required
                    value={draft.endedFrom}
                    onChange={event => change('endedFrom', event.target.value)}
                  />
                </label>
              ) : (
                <>
                  <label
                    className="client-discounts__field client-discounts__field--4"
                    htmlFor="ClientDiscounts-field-2"
                  >
                    {t('kind')}
                    <select
                      id="ClientDiscounts-field-2"
                      className="form-control"
                      required
                      value={draft.typeId}
                      onChange={event => {
                        const typeId = event.target.value;
                        const type = types.find(item => item.typeId === typeId);
                        setDraft(current => ({ ...current, typeId, percent: type ? String(type.defaultPercentBasisPoints / 100) : '', prepaymentConfirmed: false }));
                      }}
                    >
                      <option value="">{t('choose')}</option>
                      {types.map(type => (
                        <option
                          key={type.typeId}
                          value={type.typeId}
                        >{type.name}{type.endedFrom ? ` (${t('endedFrom')}: ${displayDate(type.endedFrom)})` : ''}
                        </option>
                      ))}
                    </select>

                  </label>
                  <label
                    className="client-discounts__field client-discounts__field--2"
                    htmlFor="ClientDiscounts-field-3"
                  >
                    {t('percent')}
                    <input
                      id="ClientDiscounts-field-3"
                      className="form-control"
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      required
                      value={draft.percent}
                      onChange={event => change('percent', event.target.value)}
                    />
                  </label>
                  <label
                    className="client-discounts__field client-discounts__field--3"
                    htmlFor="ClientDiscounts-field-4"
                  >
                    {t('from')}
                    <DateField
                      id="ClientDiscounts-field-4"
                      aria-label={t('from')}
                      className="form-control"
                      type="date"
                      required
                      value={draft.validFrom}
                      onChange={event => change('validFrom', event.target.value)}
                    />
                  </label>
                  <label
                    className="client-discounts__field client-discounts__field--3"
                    htmlFor="ClientDiscounts-field-5"
                  >
                    {t('to')}
                    <DateField
                      id="ClientDiscounts-field-5"
                      aria-label={t('to')}
                      className="form-control"
                      type="date"
                      required={!!(selectedType && selectedType.requiresValidTo)}
                      min={draft.validFrom}
                      value={draft.validTo}
                      onChange={event => change('validTo', event.target.value)}
                    />
                  </label>
                  <label
                    className="client-discounts__field client-discounts__field--4"
                    htmlFor="ClientDiscounts-field-6"
                  >
                    {t('scope')}
                    <select
                      id="ClientDiscounts-field-6"
                      className="form-control"
                      value={draft.studentScope}
                      onChange={event => change('studentScope', event.target.value)}
                    >
                      <option value="all">{t('all')}</option>
                      <option value="selected">{t('selected')}</option>
                    </select>
                  </label>
                </>
              ))}
              <label
                className="client-discounts__field client-discounts__field--8"
                htmlFor="ClientDiscounts-field-9"
              >
                {t('reason')}
                <textarea
                  id="ClientDiscounts-field-9"
                  className="form-control"
                  required
                  rows={1}
                  maxLength={1000}
                  value={draft.reason}
                  onChange={event => change('reason', event.target.value)}
                />
              </label>
              {!['end', 'delete'].includes(draft.intent) && (
              <>
                {draft.studentScope === 'selected' && (
                <div className="client-discounts__students">
                  {students.map(student => (
                    <label
                      key={student.id}
                      className="mb-0"
                      htmlFor={`discount-student-${student.id}`}
                    >

                      <input
                        id={`discount-student-${student.id}`}
                        type="checkbox"
                        checked={draft.studentIds.includes(student.id)}
                        onChange={event => change('studentIds', event.target.checked ? [...draft.studentIds, student.id] : draft.studentIds.filter(id => id !== student.id))}
                      /> {student.firstName} {student.lastName}
                    </label>
                  ))}
                </div>
                )}
                {selectedType && selectedType.requiresPrepayment && (
                <label
                  className="client-discounts__field client-discounts__field--12"
                  htmlFor="ClientDiscounts-field-8"
                >

                  <input
                    id="ClientDiscounts-field-8"
                    type="checkbox"
                    required
                    checked={draft.prepaymentConfirmed}
                    onChange={event => change('prepaymentConfirmed', event.target.checked)}
                  /> {t('confirmPrepayment')}
                </label>
                )}

              </>
              )}
            </div>
            {!!errors.length && (
              <div className="alert alert-warning" id="discount-validation" aria-live="polite">
                <strong>{t('validation.title')}</strong>
                <ul className="mb-0">
                  {errors.map(error => (
                    <li key={error.message}>
                      <a href={`#ClientDiscounts-field-${error.field}`}>{error.message}</a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="client-discounts__actions">
              <button
                type="button"
                className="btn manage-button manage-teal-button"
                onClick={() => setDraft(null)}
              >{tr('common:cancel')}
              </button>
              <button
                type="submit"
                className="btn manage-button manage-red-button"
                aria-describedby={errors.length ? 'discount-validation' : undefined}
                disabled={!valid || ['conflict', 'refresh', 'alreadyUsed'].includes(policy.error)}
              >{draft.intent === 'delete' ? t('confirmDelete') : tr('common:save')}
              </button>
            </div>
          </fieldset>
        </form>
        )}
        {policy.data && policy.data.grants.versions.length > 0 && (
        <details
          className="client-discounts__history"
        ><summary>{t('history')}</summary>{policy.data.grants.versions.map(grant => (
          <p
            key={`${grant.grantId}-${grant.version}`}
          >{grant.typeId} · {t('version')} {grant.version} · {grant.percentBasisPoints / 100}% · {displayDate(grant.validFrom)} — {displayDate(grant.validTo) || t('openEnd')}
            {grant.deletedAt && ` · ${t('deleted')}: ${displayDateTime(grant.deletedAt)} · ${grant.deletionReason}`}
            {grant.endedFrom && ` · ${t('endedFrom')}: ${displayDate(grant.endedFrom)}`} · {grant.reason} · {grant.actorId} · {displayDateTime(grant.recordedAt)}
          </p>
        ))}
        </details>
        )}
      </div>
    </section>
  );
}
ClientDiscounts.propTypes = { clientId: PropTypes.string.isRequired, students: PropTypes.array, user: PropTypes.object, service: PropTypes.object };
ClientDiscounts.defaultProps = { students: [], user: null, service: ClientDiscountService };
